import React from 'react';
import { Chip, Tooltip } from '@biq/apex-ui';
import { Box } from '@mui/material';
import { JobStatus, JobSummary, JobType, Result } from '../../../../lib';
import DownloadJobMenu from '../download-job-menu/DownloadJobMenu';
import { CancelJobMenu } from '../cancel-job-menu/CancelJobMenu';

type Column = {
    field: string;
    headerName: string;
    editable: boolean;
    resizable?: boolean;
    flex?: number;
    width?: number;
    headerClassName: string;
    renderCell?: (params: any) => React.JSX.Element;
    valueGetter?: any;
};

interface DownloadedJobStatus {
    downloadedDate?: string;
    downloadedWalletId?: string;
}
export interface JobStatusProps {
    status: JobStatus;
    jobId: string;
    jobType: JobType;
    results: Result & DownloadedJobStatus;
}

interface JobRow {
    jobCreated: string;
    jobCompleted: string;
    loggedInId: string;
    dictName: string;
    id: number;
    statusProps?: JobStatusProps;
}

export type JobRowData = JobSummary & JobRow;

const getChipColorAndText = (
    status: JobStatus
): {
    text: string;
    color:
        | 'default'
        | 'secondary'
        | 'primary'
        | 'success'
        | 'error'
        | 'info'
        | 'warning'
        | 'grayscale';
    styles?: Object;
} => {
    if (status === 'COMPLETED_SUCCESS') {
        return {
            text: 'COMPLETE',
            color: 'secondary',
        };
    } else if (status === 'COMPLETED_ERROR' || status === 'INCOMPLETE_ERROR') {
        return {
            text: 'ERROR',
            color: 'error',
        };
    } else if (status === 'QUEUED') {
        return {
            text: 'QUEUED',
            color: 'success',
        };
    } else if (status === 'RUNNING') {
        return {
            text: 'RUNNING',
            color: 'default',
            styles: {
                backgroundColor: '#00A8BE',
                color: '#EDFDFF',
            },
        };
    } else if (status === 'CANCELLED') {
        return {
            text: 'CANCELLED',
            color: 'grayscale',
        };
    }
    return {
        text: 'NEW',
        color: 'grayscale',
    };
};

export const getChipDisplay = (status: JobStatus): React.JSX.Element => {
    const { text, color, styles } = getChipColorAndText(status);
    return (
        <Chip
            label={text}
            color={color}
            size='small'
            variant='filled'
            sx={styles}
            readOnly={true}
        />
    );
};

const getCurrentJobDownloadId = (value: any): string => {
    return value?.jobId ?? '';
};

const isDownloadButtonEnabledForCurrentJob = (value: any): boolean => {
    return (
        !value?.downloadedDate &&
        !value?.downloadedWalletId &&
        !value?.results?.downloadResults?.error
    );
};

export const getColumns = (
    openJobId: string | undefined,
    handleMenuOpen: any,
    downloadSelectedJob: (id: string | undefined) => void,
    hasAllowedFileDownloadRoles: boolean,
    refreshJobList: Function
): Column[] => {
    return [
        {
            field: 'formattedJobType',
            headerName: 'Job Type',
            editable: false,
            flex: 1,
            headerClassName: 'jobs-grid-header',
            renderCell: (params: any) => (
                <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
                    {params.value}
                </Box>
            ),
        },
        {
            field: 'jobId',
            headerName: 'Job ID',
            editable: false,
            flex: 1,
            headerClassName: 'jobs-grid-header',
        },
        {
            field: 'dictName',
            headerName: 'Dictionary',
            editable: false,
            flex: 1,
            headerClassName: 'jobs-grid-header',
        },
        {
            field: 'loggedInId',
            headerName: 'Created By',
            width: 150,
            editable: false,
            flex: 1,
            headerClassName: 'jobs-grid-header',
        },
        {
            field: 'formattedJobCreated',
            headerName: 'Created On',
            width: 150,
            editable: false,
            flex: 1,
            headerClassName: 'jobs-grid-header',
        },
        {
            field: 'formattedJobCompleted',
            headerName: 'Completed On',
            width: 150,
            editable: false,
            flex: 1,
            headerClassName: 'jobs-grid-header',
        },
        {
            field: 'statusProps',
            headerName: 'Status',
            width: 150,
            editable: false,
            resizable: false,
            headerClassName: 'jobs-grid-header',
            valueGetter: (params: any) => params.status,
            renderCell: (params: any): any => (
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <Tooltip
                        title={'Refresh page to see the updated status.'}
                        arrow
                        placement='top'
                        slotProps={{
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        width: '104px',
                                    },
                                },
                            },
                        }}
                    >
                        <Box component={'span'}>
                            {getChipDisplay(params.row.status)}
                        </Box>
                    </Tooltip>
                    {(params.row.statusProps.jobType === 'FileDownloadJob' ||
                        params.row.statusProps.jobType ===
                            'GlossaryDownloadJob' ||
                        params.row.statusProps.jobType ===
                            'ExportDownloadJob') &&
                        params.row.status === 'COMPLETED_SUCCESS' &&
                        hasAllowedFileDownloadRoles && (
                            <DownloadJobMenu
                                openJobId={openJobId}
                                onMenuOpen={handleMenuOpen}
                                downloadSelectedJob={downloadSelectedJob}
                                jobDownloadId={getCurrentJobDownloadId(
                                    params.row
                                )}
                                isDownloadEnabled={isDownloadButtonEnabledForCurrentJob(
                                    params.row
                                )}
                            />
                        )}
                    {(params.row.status === 'NEW' ||
                        params.row.status === 'QUEUED' ||
                        params.row.status === 'RUNNING') && (
                        <CancelJobMenu
                            jobId={params.row.statusProps.jobId}
                            refreshJobList={refreshJobList}
                        />
                    )}
                </Box>
            ),
        },
    ];
};
